import { useMemo } from 'react';
import Card, { CardHeader, CardBody } from '../ui/Card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface CashFlowData {
  // Operating Activities
  netCashFromOperations?: string | null;
  netIncome?: string | null;
  depreciationAmortization?: string | null;
  stockBasedCompensation?: string | null;
  deferredTaxes?: string | null;
  workingCapitalChanges?: string | null;
  // Investing Activities
  netCashFromInvesting?: string | null;
  capitalExpenditures?: string | null;
  acquisitions?: string | null;
  investmentPurchases?: string | null;
  investmentSales?: string | null;
  // Financing Activities
  netCashFromFinancing?: string | null;
  debtIssuance?: string | null;
  debtRepayment?: string | null;
  stockIssuance?: string | null;
  stockRepurchase?: string | null;
  dividendsPaid?: string | null;
  // Summary
  netChangeInCash?: string | null;
  freeCashFlow?: string | null;
}

interface Report {
  reportId: string;
  period: string;
  type: string;
  cashFlow?: CashFlowData;
}

interface ComparativeCashFlowTableProps {
  reports: Report[];
  mode: 'single' | 'annual' | 'quarterly';
  maxPeriods: number; // 3, 5, or 10
}

interface ChangeIndicator {
  percentage: number;
  direction: 'up' | 'down' | 'neutral';
  color: string;
}

// Parse currency string to number
const parseCurrency = (value: string | null | undefined): number | null => {
  if (!value) return null;
  // Remove $, commas, B/M suffixes
  const cleaned = value.replace(/[$,\s]/g, '');

  let multiplier = 1;
  if (cleaned.includes('B')) {
    multiplier = 1_000_000_000;
  } else if (cleaned.includes('M')) {
    multiplier = 1_000_000;
  } else if (cleaned.includes('K')) {
    multiplier = 1_000;
  }

  const numStr = cleaned.replace(/[BMK]/g, '');
  const num = parseFloat(numStr);

  return isNaN(num) ? null : num * multiplier;
};

// Calculate percentage change
const calculateChange = (current: string | null | undefined, previous: string | null | undefined): ChangeIndicator | null => {
  const currentVal = parseCurrency(current);
  const previousVal = parseCurrency(previous);

  if (currentVal === null || previousVal === null || previousVal === 0) {
    return null;
  }

  const change = ((currentVal - previousVal) / Math.abs(previousVal)) * 100;

  return {
    percentage: change,
    direction: Math.abs(change) < 0.5 ? 'neutral' : change > 0 ? 'up' : 'down',
    color: Math.abs(change) < 0.5 ? 'text-neutral-600' : change > 0 ? 'text-success-600' : 'text-error-600'
  };
};

// Format value for display
const formatValue = (value: string | null | undefined): string => {
  if (!value) return 'N/A';
  return value;
};

// Check if a field has at least one non-null value across all reports
const hasAnyValue = (reports: Report[], field: string): boolean => {
  return reports.some(r => {
    const val = r.cashFlow?.[field as keyof CashFlowData];
    return val != null && val !== '';
  });
};

export default function ComparativeCashFlowTable({
  reports,
  mode,
  maxPeriods
}: ComparativeCashFlowTableProps) {
  // Filter and limit reports based on mode and maxPeriods
  const displayedReports = useMemo(() => {
    let filtered: Report[];

    if (mode === 'annual') {
      filtered = reports.filter(r => r.type === 'Annual' || r.period.includes('FY'));
    } else if (mode === 'quarterly') {
      filtered = reports.filter(r => r.type === 'Quarterly' || r.period.match(/Q[1-4]/));
    } else {
      // Single mode - just latest
      filtered = reports.slice(0, 1);
    }

    // Limit to maxPeriods
    return filtered.slice(0, maxPeriods);
  }, [reports, mode, maxPeriods]);

  // Define all rows with hierarchy
  const allRows = useMemo(() => [
    // Operating Activities Section
    { type: 'header', label: 'OPERATING ACTIVITIES', field: null },
    { type: 'item', label: 'Net Income', field: 'netIncome' },
    { type: 'item', label: 'Depreciation & Amortization', field: 'depreciationAmortization' },
    { type: 'item', label: 'Stock-Based Compensation', field: 'stockBasedCompensation' },
    { type: 'item', label: 'Deferred Taxes', field: 'deferredTaxes' },
    { type: 'item', label: 'Working Capital Changes', field: 'workingCapitalChanges' },
    { type: 'total', label: 'Net Cash from Operations', field: 'netCashFromOperations' },

    // Investing Activities Section
    { type: 'header', label: 'INVESTING ACTIVITIES', field: null },
    { type: 'item', label: 'Capital Expenditures', field: 'capitalExpenditures' },
    { type: 'item', label: 'Acquisitions', field: 'acquisitions' },
    { type: 'item', label: 'Investment Purchases', field: 'investmentPurchases' },
    { type: 'item', label: 'Investment Sales', field: 'investmentSales' },
    { type: 'total', label: 'Net Cash from Investing', field: 'netCashFromInvesting' },

    // Financing Activities Section
    { type: 'header', label: 'FINANCING ACTIVITIES', field: null },
    { type: 'item', label: 'Debt Issuance', field: 'debtIssuance' },
    { type: 'item', label: 'Debt Repayment', field: 'debtRepayment' },
    { type: 'item', label: 'Stock Issuance', field: 'stockIssuance' },
    { type: 'item', label: 'Stock Repurchase', field: 'stockRepurchase' },
    { type: 'item', label: 'Dividends Paid', field: 'dividendsPaid' },
    { type: 'total', label: 'Net Cash from Financing', field: 'netCashFromFinancing' },

    // Summary Section
    { type: 'header', label: 'SUMMARY', field: null },
    { type: 'total', label: 'Net Change in Cash', field: 'netChangeInCash' },
    { type: 'total', label: 'Free Cash Flow', field: 'freeCashFlow' },
  ], []);

  // Filter out item rows that have NO data across all displayed reports
  const rows = useMemo(() => {
    return allRows.filter(row => {
      if (!row.field) return true; // always keep section headers
      if (row.type === 'total') return true; // always keep totals
      return hasAnyValue(displayedReports, row.field);
    });
  }, [allRows, displayedReports]);

  // Render cell with change indicator
  const renderCell = (report: Report, field: string | null, columnIndex: number) => {
    if (!field) return null;

    const value = report.cashFlow?.[field as keyof CashFlowData];
    const previousReport = displayedReports[columnIndex + 1];

    // Calculate change vs previous period
    let changeIndicator: ChangeIndicator | null = null;
    if (mode !== 'single' && previousReport) {
      const previousValue = previousReport.cashFlow?.[field as keyof CashFlowData];
      changeIndicator = calculateChange(value, previousValue);
    }

    return (
      <div className="flex items-center justify-end gap-2">
        <span>{formatValue(value)}</span>
        {changeIndicator && (
          <span className={`flex items-center text-xs font-medium ${changeIndicator.color}`}>
            {changeIndicator.direction === 'up' && <TrendingUp className="h-3 w-3 mr-0.5" />}
            {changeIndicator.direction === 'down' && <TrendingDown className="h-3 w-3 mr-0.5" />}
            {changeIndicator.direction === 'neutral' && <Minus className="h-3 w-3 mr-0.5" />}
            {Math.abs(changeIndicator.percentage).toFixed(1)}%
          </span>
        )}
      </div>
    );
  };

  // Get row styling based on type
  const getRowClass = (type: string) => {
    switch (type) {
      case 'header':
        return 'bg-neutral-100 font-bold text-neutral-900';
      case 'category':
        return 'bg-primary-50 font-semibold text-primary-900';
      case 'total':
        return 'bg-neutral-50 font-bold text-neutral-900 border-t-2 border-neutral-300';
      case 'item':
        return 'text-neutral-700';
      default:
        return '';
    }
  };

  const getLabelClass = (type: string) => {
    switch (type) {
      case 'header':
        return 'py-3 px-4';
      case 'category':
        return 'py-2 px-4 pl-6';
      case 'total':
        return 'py-3 px-4 pl-6';
      case 'item':
        return 'py-2 px-4 pl-10';
      default:
        return 'py-2 px-4';
    }
  };

  if (displayedReports.length === 0) {
    return (
      <Card>
        <CardBody className="text-center py-8 text-neutral-600">
          No cash flow data available
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="h-full">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-semibold text-neutral-900">
              Cash Flow Statement {mode === 'single' ? 'Detail' : 'Comparison'}
            </h3>
            <p className="text-sm text-neutral-600 mt-1">
              {mode === 'annual' && `Showing ${displayedReports.length} Annual Report${displayedReports.length > 1 ? 's' : ''}`}
              {mode === 'quarterly' && `Showing ${displayedReports.length} Quarter${displayedReports.length > 1 ? 's' : ''}`}
              {mode === 'single' && displayedReports[0]?.period}
            </p>
          </div>
          {mode !== 'single' && (
            <div className="text-xs text-neutral-500">
              <span className="text-success-600 font-medium">↑ Increase</span>
              {' · '}
              <span className="text-error-600 font-medium">↓ Decrease</span>
            </div>
          )}
        </div>
      </CardHeader>
      <CardBody className="p-0">
        <div
          className="overflow-x-auto"
          style={{
            scrollbarWidth: 'thin',
            scrollbarColor: '#cbd5e1 transparent',
          }}
        >
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-white border-b-2 border-neutral-300 z-10">
              <tr>
                <th className="text-left py-3 px-4 font-semibold text-neutral-700 min-w-[250px]">
                  Category
                </th>
                {displayedReports.map((report, idx) => (
                  <th key={report.reportId} className="text-right py-3 px-4 font-semibold text-neutral-700 min-w-[150px]">
                    <div className="flex flex-col items-end">
                      <span>{report.period}</span>
                      {idx === 0 && mode !== 'single' && (
                        <span className="text-xs font-normal text-primary-600">Latest</span>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => {
                const rowClass = getRowClass(row.type);
                const labelClass = getLabelClass(row.type);

                // For header rows without field, span all columns
                if (!row.field) {
                  return (
                    <tr key={rowIndex} className={rowClass}>
                      <td colSpan={displayedReports.length + 1} className={labelClass}>
                        {row.label}
                      </td>
                    </tr>
                  );
                }

                return (
                  <tr key={rowIndex} className={`${rowClass} hover:bg-neutral-50`}>
                    <td className={labelClass}>{row.label}</td>
                    {displayedReports.map((report, colIndex) => (
                      <td key={report.reportId} className="py-2 px-4 text-right">
                        {renderCell(report, row.field, colIndex)}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </CardBody>
    </Card>
  );
}
